
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Profile } from "@/integrations/supabase/types/subscription";
import { User } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface PersonalInfoCardProps {
  profile: Profile;
  onUpdate: (updates: Partial<Profile>) => Promise<void>;
}

export function PersonalInfoCard({ profile, onUpdate }: PersonalInfoCardProps) {
  const [fullName, setFullName] = useState(profile.full_name || "");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onUpdate({ full_name: fullName });
      toast({
        title: "Profil oppdatert",
        description: "Dine endringer har blitt lagret",
      });
    } catch (error) {
      console.error("Feil ved oppdatering av profil:", error);
      toast({
        title: "Feil ved oppdatering",
        description: "Kunne ikke lagre endringer. Vennligst prøv igjen.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="transition-all duration-300 hover:shadow-lg bg-white/50 backdrop-blur-sm">
      <CardHeader className="space-y-1 p-4 sm:p-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 rounded-full bg-orange-100 flex-shrink-0">
            <User className="h-5 w-5 text-orange-600" />
          </div>
          <div>
            <CardTitle className="text-lg sm:text-xl text-orange-900">
              Personlig Informasjon
            </CardTitle>
            <CardDescription className="text-sm text-orange-600/80">
              Oppdater din personlige informasjon
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-sm font-medium text-orange-900">
              Fullt navn
            </Label>
            <Input
              id="fullName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className="w-full border-orange-200 focus:border-orange-500 focus:ring-orange-500"
              placeholder="Skriv inn ditt fulle navn"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium text-orange-900">
              E-post
            </Label>
            <Input
              id="email"
              type="email"
              value={profile.email || ""}
              disabled
              className="w-full bg-orange-50 border-orange-200 text-orange-700"
            />
          </div>
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              {isSubmitting ? "Lagrer..." : "Lagre endringer"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
